import { type Component, createEffect, createMemo, For, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import type { FichaModalPropsType } from '~/types/FichaModal'
import type { DraftSpecType } from '~/types/ProductForm'
import { slugify } from '~/utils/slug'

/**
 * Alta y edición de una característica de la ficha técnica. Mismo patrón que
 * `TarifaModal`: el listado sólo muestra, y editar abre este modal.
 *
 * Las sugerencias salen de lo ya cargado en la empresa. No obligan a nada —se
 * puede escribir cualquier cosa— pero son lo que hace que no acaben conviviendo
 * "Color", "Colores" y "color" como tres características distintas.
 */
const FichaModal: Component<FichaModalPropsType> = props => {
  const [draft, setDraft] = createStore<DraftSpecType>({ label: '', value: '', unit: '' })

  // Depende también de `isOpen`: al crear dos seguidas, `spec` sigue siendo null y
  // sin esto el efecto no se repetía, así que la segunda heredaba lo tecleado en
  // la primera (una unidad "cc" que ya no venía a cuento).
  createEffect(() => {
    if (!props.isOpen) return
    const spec = props.spec
    setDraft(spec ? { label: spec.label, value: spec.value, unit: spec.unit } : { label: '', value: '', unit: '' })
  })

  const isValid = () => draft.label.trim().length > 0 && draft.value.trim().length > 0

  const handleSave = () => {
    if (!isValid()) return
    props.onSave({ label: draft.label.trim(), value: draft.value.trim(), unit: draft.unit.trim() })
  }

  /** Duplicado con otra característica de la misma ficha: el backend se queda con la primera. */
  const isDuplicated = createMemo(() => {
    const key = slugify(draft.label)
    return Boolean(key) && (props.usedKeys ?? []).includes(key) && slugify(props.spec?.label ?? '') !== key
  })

  const labelSuggestions = createMemo(() => {
    const typed = slugify(draft.label)
    return (props.suggestions ?? []).filter(s => (!typed || s.key.startsWith(typed)) && s.key !== typed).slice(0, 8)
  })

  const valueSuggestions = createMemo(() => {
    const key = slugify(draft.label)
    if (!key) return []
    const typed = slugify(draft.value)
    const group = (props.suggestions ?? []).find(s => s.key === key)
    return (group?.values ?? []).filter(v => (!typed || v.value.startsWith(typed)) && v.value !== typed).slice(0, 8)
  })

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm" closable>
      <Modal.Header>
        <h2 class="text-lg font-bold text-foreground">{props.spec ? 'Editar característica' : 'Nueva característica'}</h2>
      </Modal.Header>

      <Modal.Body>
        <div class="space-y-5">
          <div>
            <Input
              label="Característica"
              value={draft.label}
              onInput={e => setDraft('label', e.currentTarget.value)}
              placeholder="Cilindrada, Material, Duración…"
              error={isDuplicated() ? 'Esta ficha ya tiene una característica con ese nombre' : undefined}
            />
            <Show when={labelSuggestions().length > 0}>
              <div class="flex flex-wrap items-center gap-1.5 mt-2">
                <span class="text-xs text-foreground-subtle">Ya usadas:</span>
                <For each={labelSuggestions()}>
                  {suggestion => (
                    <Chip size="sm" variant="soft" color="secondary" class="cursor-pointer" onClick={() => setDraft('label', suggestion.label)}>
                      {suggestion.label}
                    </Chip>
                  )}
                </For>
              </div>
            </Show>
          </div>

          <div class="flex items-start gap-3">
            <div class="flex-1">
              <Input label="Valor" value={draft.value} onInput={e => setDraft('value', e.currentTarget.value)} placeholder="199" />
            </div>
            <div class="w-24 shrink-0">
              <Input label="Unidad" value={draft.unit} onInput={e => setDraft('unit', e.currentTarget.value)} placeholder="cc" />
            </div>
          </div>

          <Show when={valueSuggestions().length > 0}>
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="text-xs text-foreground-subtle">Valores usados:</span>
              <For each={valueSuggestions()}>
                {value => (
                  <Chip size="sm" variant="soft" color="info" class="cursor-pointer" onClick={() => setDraft('value', value.label)}>
                    {value.label}
                  </Chip>
                )}
              </For>
            </div>
          </Show>

          <p class="text-xs text-foreground-subtle">Describe el producto sin crear variantes. Para talla o color con existencias propias, usa la pestaña Variantes.</p>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button variant="text" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={!isValid()}>
            {props.spec ? 'Guardar' : 'Añadir'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default FichaModal
